// 공통 차트/UI 컴포넌트

export const parseDateStr = s => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const toDateKey = d =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const weekLabel = d => {
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    return `${d.getMonth() + 1}월 ${Math.ceil((d.getDate() + first.getDay()) / 7)}주`;
};

export function EmptyState({ text }) {
    return (
        <div className="empty-state" style={{ padding: '32px 0' }}>
            <div className="empty-text">{text || '아직 기록이 없어요.'}</div>
        </div>
    );
}

export function StatCards({ items }) {
    return (
        <div className="stats-row" style={{ flexWrap: 'wrap' }}>
            {items.map((item, i) => (
                <div key={i} className="stat-card">
                    <div className="stat-val">{item.val}</div>
                    <div className="stat-label">{item.label}</div>
                </div>
            ))}
        </div>
    );
}

export function SectionCard({ title, desc, children }) {
    return (
        <section className="stats-section">
            <div className="stats-section-head">
                <div>
                    <div className="stats-section-title">{title}</div>
                    {desc && <div className="stats-section-desc">{desc}</div>}
                </div>
            </div>
            {children}
        </section>
    );
}

export function HBar({ label, value, max, color, suffix = '' }) {
    const pct = max > 0 ? Math.min(1, value / max) : 0;
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                <span>{label}</span>
                <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--text3)' }}>{value}{suffix}</span>
            </div>
            <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3 }}>
                <div style={{ height: '100%', width: `${pct * 100}%`, background: color || 'var(--accent)', borderRadius: 3, transition: 'width 0.4s ease' }} />
            </div>
        </div>
    );
}

export function MiniLineChart({ data, color = 'var(--accent)', label }) {
    if (data.length < 2) return null;
    const W = 340, H = 80, padX = 28, padY = 14;
    const vals = data.map(d => d.val);
    const minV = Math.min(...vals);
    const maxV = Math.max(...vals);
    const range = maxV - minV || 1;
    const px = i => padX + (i / (data.length - 1)) * (W - padX * 2);
    const py = v => H - padY - ((v - minV) / range) * (H - padY * 2);
    const points = data.map((d, i) => `${px(i)},${py(d.val)}`).join(' ');
    const latest = data[data.length - 1];
    const first = data[0];

    return (
        <div className="chart-wrap">
            {label && (
                <div className="chart-title">
                    <span>{label}</span>
                    <span style={{ fontFamily: 'DM Mono,monospace', color }}>{latest.val}</span>
                </div>
            )}
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 88, overflow: 'visible' }}>
                <defs>
                    <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={color} stopOpacity="0" />
                    </linearGradient>
                </defs>
                {[0, 0.5, 1].map(t => {
                    const yVal = minV + t * range;
                    return (
                        <g key={t}>
                            <line x1={padX} y1={py(yVal)} x2={W - padX} y2={py(yVal)} stroke="var(--bg3)" strokeWidth="1" />
                            <text x={padX - 4} y={py(yVal) + 4} fontSize="8" fill="var(--text3)" textAnchor="end" fontFamily="DM Mono,monospace">
                                {Math.round(yVal)}
                            </text>
                        </g>
                    );
                })}
                <polygon points={`${px(0)},${H - padY} ${points} ${px(data.length - 1)},${H - padY}`} fill={`url(#grad-${label})`} />
                <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
                {data.map((d, i) => <circle key={i} cx={px(i)} cy={py(d.val)} r="2.5" fill={color} />)}
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono,monospace' }}>
                <span>{first.label}</span>
                <span>{latest.label}</span>
            </div>
        </div>
    );
}

export function WeeklyBarChart({ data, color = 'var(--accent)', label }) {
    const maxW = Math.max(...data.map(w => w.val), 1);
    return (
        <div className="chart-wrap">
            <div className="chart-title">
                <span>{label}</span>
                <span style={{ fontFamily: 'DM Mono,monospace', color }}>{data[data.length - 1]?.val ?? 0}</span>
            </div>
            <div className="bar-chart" style={{ alignItems: 'flex-end', gap: 4 }}>
                {data.map((w, i) => {
                    const isLatest = i === data.length - 1;
                    const h = Math.round((w.val / maxW) * 72) + 4;
                    return (
                        <div key={i} className="chart-bar-wrap">
                            {w.val > 0 && (
                                <span style={{ fontSize: 10, fontFamily: 'DM Mono,monospace', color: isLatest ? color : 'var(--text2)', marginBottom: 3 }}>
                                    {w.val}
                                </span>
                            )}
                            <div className="chart-bar" style={{ height: h, background: w.val ? (isLatest ? color : 'var(--bg4)') : 'var(--bg3)' }} />
                            <div className="chart-label" style={{ color: isLatest ? 'var(--text2)' : 'var(--text3)' }}>{w.label}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function last8Weeks(records, valueFn) {
    return Array.from({ length: 8 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (7 - i) * 7);
        const wS = new Date(d); wS.setDate(d.getDate() - d.getDay()); wS.setHours(0, 0, 0, 0);
        const wE = new Date(wS); wE.setDate(wS.getDate() + 6); wE.setHours(23, 59, 59, 999);
        const weekRecs = records.filter(r => { const rd = parseDateStr(r.date); return rd >= wS && rd <= wE; });
        return { label: weekLabel(wS), val: valueFn ? valueFn(weekRecs) : weekRecs.length };
    });
}
